import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
	Container,
	Grid,
	Typography,
	Button,
	Card,
	CardContent,
	CardActions,
	Dialog,
	Box,
	Chip,
	Skeleton,
	styled,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { motion } from 'framer-motion'
import axios from 'axios'
import RouteTrips from '../Booking'
import { withStyles } from '@mui/styles'

import MuiDialogTitle from '@mui/material/DialogTitle'
import MuiDialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { BusFront, CalendarDays } from 'lucide-react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
const saccoLogoMap = {
	'a8d7fd86-e7b2-43eb-8d19-843a67ab552a': '/images/MolineLuxuryLogo.png', // Moline Luxury Shuttle
	'72a1e4e1-cd20-4663-858a-efccc1aeee0a': '/images/ExpressShuttle-Primary.png', // Express Shuttle
	'5d1f7fd1-2179-45aa-a024-aeaac76f36c7': '/images/BometTravellers.png', // Bomet Travellers
	'016a4a62ab5b6e49df': '/images/transline.jpg', // Transline
	'7710344a-7696-440e-b21e-75dc01ab9822': '/images/Cherangani.png', // Cherangani Travellers Sacco
	'2e8698ed-d9fe-4892-9646-733fa1bcaa6e': '/images/narokLine.png', // Narok Line
}

const ProviderLogo = styled(motion.img)(({ theme }) => ({
	height: '36px',
	cursor: 'pointer',
	[theme.breakpoints.down('sm')]: {
		height: '88px', // Smaller height for mobile screens
	},
	[theme.breakpoints.down('sm')]: {
		height: '88px', // Smaller height for mobile screens
	},
	[theme.breakpoints.up('md')]: {
		height: '88px', // Larger height for medium screens and above
	},
	[theme.breakpoints.up('lg')]: {
		height: '88px', // Even larger for large screens
	},
}))

const styles = theme => ({
	root: {
		margin: 0,
		padding: theme.spacing(2),
	},
	closeButton: {
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(1),
		color: theme.palette.grey[500],
	},
})

const DialogTitle = withStyles(styles)(props => {
	const { children, classes, onClose, ...other } = props
	return (
		<MuiDialogTitle disableTypography className={classes.root} {...other}>
			<Typography variant="h4" style={{ fontWeight: '700' }}>
				{children}
			</Typography>
			{onClose ? (
				<IconButton
					aria-label="close"
					className={classes.closeButton}
					onClick={onClose}
				>
					<CloseIcon />
				</IconButton>
			) : null}
		</MuiDialogTitle>
	)
})
const DialogContent = withStyles(theme => ({
	root: {
		padding: theme.spacing(2),
	},
}))(MuiDialogContent)

const useStyles = makeStyles(theme => ({
	root: {
		flexGrow: 1,
		backgroundColor: '#fff',
		padding: theme.spacing(2),
		// '& > svg': {
		// 	margin: theme.spacing(2),
		// },
	},
	tripCard: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		// maxWidth: '800px',
		margin: 'auto',
		padding: theme.spacing(1),
		borderRadius: theme.spacing(1),
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[3],
		gap: theme.spacing(3),
		transition: 'transform 0.3s ease',
		'&:hover': {
			transform: 'scale(1.03)',
		},
	},
	mainCardInfo: {
		flex: 1,
		display: 'flex',
		flexDirection: 'row',
		gap: theme.spacing(1),
	},
	tripDetails: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(4),
	},
	detailItem: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		color: theme.palette.text.primary,
	},
	bookButton: {
		whiteSpace: 'nowrap',
		padding: theme.spacing(1, 3),
		fontWeight: theme.typography.fontWeightBold,
	},
}))

const Trips = () => {
	const classes = useStyles()
	const [trips, setTrips] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [open, setOpen] = useState(false)
	const [selectedRoute, setSelectedRoute] = useState(null)
	const navigate = useNavigate()

	const fetchFeaturedTrips = useCallback(async () => {
		try {
			setLoading(true)
			setError(null) // Clear any previous errors

			const token =
				'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiJhNzI5YTI3ZS1hMzI5LTRiYzAtOTYwOC1mZGQxYzM3YWRlZjYiLCJpYXQiOjE1ODAyMTA1MzQsImV4cCI6MTg1MDIxMDUzNH0.OZtT-9gQAOi3RR1pBhR2U9B2RbUMGRG7uGN92J-JO8I'

			// const departureDate = new Date()
			// departureDate.setHours(23, 59, 59, 999)

			const response = await axios.get(
				`${import.meta.env.VITE_BASE_URL}bookingMobile/trips/featuredTrips`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			)

			const groupedTrips = response.data.data

			// Flatten all trips from the object into a single array
			const flatTrips = Object.values(groupedTrips).flat()

			// Only update state if data has changed
			setTrips(prevTrips => {
				if (JSON.stringify(prevTrips) !== JSON.stringify(flatTrips)) {
					return flatTrips
				}
				return prevTrips
			})

			return flatTrips
		} catch (err) {
			setError('Failed to fetch trips. Please try again.')
			return []
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchFeaturedTrips()
	}, [fetchFeaturedTrips])

	// const handleBook = useCallback(route => {
	// 	setSelectedRoute(route)
	// 	navigate(`/trips/${route.from}-${route.to}`)
	// 	// setOpen(true)
	// }, [navigate])
	const handleBook = useCallback(
		trip => {
			navigate(`/app/booking`, {
				state: {
					from: trip.departure,
					to: trip.destination,
					selectedDate: trip.departureTime,
					passengers: 1,
					transportType: 'bus',
				},
			})
		},
		[navigate],
	)

	const handleClose = useCallback(() => {
		setOpen(false)
	}, [])
	const logoVariants = {
		initial: { opacity: 0, x: -20 },
		animate: {
			opacity: 1,
			x: 0,
			transition: {
				duration: 0.6,
			},
		},
	}

	const getSaccoLogo = appid => {
		return saccoLogoMap[appid] || null
	}

	console.log('trips', trips.length)

	// Memoize the trip rendering to prevent unnecessary re-renders
	// const tripList = useMemo(
	// 	() =>
	// 		trips &&
	// 		trips.length > 0 &&
	// 		trips.map((trip, index) => {
	// 			return (
	// 				<Grid item xs={12} md={12} key={index}>
	// 					<Card
	// 						// className={classes.tripCard}
	// 						elevation={3}
	// 						sx={{
	// 							display: 'flex',
	// 							gap: 2,
	// 							alignItems: 'center',
	// 							paddingRight: '16px',
	// 							paddingTop: '16px',
	// 							paddingBottom: '16px',
	// 						}}
	// 					>
	// 						<CardContent
	// 							// className={classes.mainCardInfo}
	// 							sx={{
	// 								width: '100%',
	// 								display: 'flex',
	// 								flexDirection: 'row',
	// 								alignItems: 'center',
	// 								justifyContent: 'space-between',
	// 								gap: 4,
	// 							}}
	// 						>
	// 							<Box sx={{ display: 'flex', gap: 2 }}>
	// 								<BusFront
	// 									style={{
	// 										alignSelf: 'center',
	// 										marginBottom: '4px',
	// 										color: '#ef404f',
	// 									}}
	// 								/>
	// 								<Box sx={{ display: 'flex' }}>
	// 									<Typography
	// 										variant="h6"
	// 										color="#000"
	// 										sx={{ fontWeight: 600 }}
	// 									>
	// 										{trip.from}
	// 									</Typography>
	// 									<Typography
	// 										variant="h6"
	// 										color="rgba(0,0,0,0.2)"
	// 										sx={{ fontWeight: 400, marginLeft: '2px' }}
	// 									>
	// 										-----
	// 									</Typography>
	// 								</Box>
	// 								<Typography
	// 									variant="h6"
	// 									color="#000"
	// 									sx={{ fontWeight: 600 }}
	// 								>
	// 									to
	// 								</Typography>
	// 								<Box sx={{ display: 'flex' }}>
	// 									<Typography
	// 										variant="h6"
	// 										color="rgba(0,0,0,0.2)"
	// 										sx={{ fontWeight: 400, marginRight: '2px' }}
	// 									>
	// 										-----
	// 									</Typography>
	// 									<Typography
	// 										variant="h6"
	// 										color="#000"
	// 										sx={{ fontWeight: 600 }}
	// 									>
	// 										{trip.to}
	// 									</Typography>
	// 								</Box>
	// 							</Box>

	// 							<div
	// 								// className={classes.tripDetails}
	// 								style={{
	// 									display: 'flex',
	// 									flexDirection: 'row',
	// 									gap: '12px',
	// 									alignItems: 'center',
	// 								}}
	// 							>
	// 								<Chip
	// 									icon={
	// 										<BusFront
	// 											style={{
	// 												fontSize: 14,
	// 												verticalAlign: 'middle',
	// 												color: 'f9a84e',
	// 											}}
	// 										/>
	// 									}
	// 									label={`${
	// 										trip.trips.length === 1
	// 											? `${trip.trips.length} Trip`
	// 											: `${trip.trips.length} Trips`
	// 									}`}
	// 									sx={{ borderRadius: '6px' }}
	// 								/>
	// 								<Chip
	// 									icon={
	// 										<CalendarDays
	// 											style={{
	// 												fontSize: 13,
	// 												verticalAlign: 'middle',
	// 												color: 'f9a84e',
	// 											}}
	// 										/>
	// 									}
	// 									label={`${moment().format('ll')}`}
	// 									sx={{ borderRadius: '6px' }}
	// 								/>
	// 								<Chip
	// 									icon={
	// 										<BusFront
	// 											style={{
	// 												fontSize: 14,
	// 												verticalAlign: 'middle',
	// 												color: 'f9a84e',
	// 											}}
	// 										/>
	// 									}
	// 									label={`${Number(trip.availableSeats)} available seats`}
	// 									sx={{ borderRadius: '6px' }}
	// 								/>
	// 							</div>
	// 							<Typography variant="h6" color="#ef404f">
	// 								From{' '}
	// 								{trip.trips.length > 0
	// 									? new Intl.NumberFormat('en-KE', {
	// 											style: 'currency',
	// 											currency: 'KES',
	// 									  }).format(Math.min(...trip.trips.map(t => t.minPrice)))
	// 									: 'N/A'}
	// 							</Typography>
	// 						</CardContent>
	// 						{/* <CardActions> */}
	// 						<Button
	// 							onClick={() => handleBook(trip)}
	// 							variant="contained"
	// 							color="secondary"
	// 							sx={{ width: '160px' }}
	// 						>
	// 							Book Now
	// 						</Button>
	// 						{/* </CardActions> */}
	// 					</Card>
	// 				</Grid>
	// 			)
	// 		}),
	// 	[trips, classes, handleBook],
	// )

	const tripList = useMemo(() => {
		// if (!Array.isArray(trips) || trips.length === 0) {
		// 	return (
		// 		<Grid item xs={12} md={12}>
		// 			<Box
		// 				sx={{
		// 					display: 'flex',
		// 					justifyContent: 'center',
		// 					alignItems: 'center',
		// 					flexDirection: 'column',
		// 					gap: 2,
		// 					padding: 4,
		// 				}}
		// 			>
		// 				<Typography
		// 					variant="h6"
		// 					sx={{
		// 						fontWeight: '500',
		// 						color: 'rgba(0,0,0,0.7)',
		// 						textAlign: 'center',
		// 					}}
		// 				>
		// 					No trips available
		// 				</Typography>
		// 				<Typography
		// 					variant="body2"
		// 					sx={{
		// 						color: 'rgba(0,0,0,0.5)',
		// 						textAlign: 'center',
		// 					}}
		// 				>
		// 					Please adjust your filters and try again later.
		// 				</Typography>
		// 			</Box>
		// 		</Grid>
		// 	)
		// }

		// Sort trips: Nairobi departures first, then alphabetically by departure
		const sortedTrips = [...trips].sort((a, b) => {
			// If one trip is from Nairobi and the other isn't, prioritize Nairobi
			if (a.departure === 'Nairobi' && b.departure !== 'Nairobi') {
				return -1
			}
			if (a.departure !== 'Nairobi' && b.departure === 'Nairobi') {
				return 1
			}
			// If both are from Nairobi or both are not from Nairobi, sort alphabetically
			return a.departure.localeCompare(b.departure)
		})

		return sortedTrips.map((trip, index) => (
			<Grid item xs={12} md={12} key={index}>
				<Card
					// className={classes.tripCard}
					// elevation={3}
					sx={{
						display: 'flex',
						gap: 2,
						height: '112px',
						justifyContent: 'center',
						maxWidth: '980px',
						margin: 'auto',
						border: '1px solid rgba(0,0,0,0.1)',
						boxShadow: '0px 8px 32px 0px rgba(17, 34, 17, 0.05)',
					}}
				>
					<CardContent
						sx={{
							width: '100%',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							padding: '16px !important',
							gap: 2,
						}}
					>
						{/* <Box
									sx={{
										display: 'flex',
										marginTop: '8px',
									}}
								>
									<ProviderLogo
										src="/images/modernCoast.svg"
										alt="logo"
										variants={logoVariants}
									/>
								</Box> */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
							}}
						>
							{getSaccoLogo(trip.appid) ? (
								<ProviderLogo
									src={getSaccoLogo(trip.appid)}
									alt={`${trip.saccoName} logo`}
									variants={logoVariants}
								/>
							) : (
								<Box
									sx={{
										width: '97px',
										height: '88px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										backgroundColor: '#e0e0e0',
										borderRadius: '8px',
										fontSize: '16px',
										fontWeight: '600',
										color: '#333',
										textAlign: 'center',
									}}
								>
									{trip.saccoName?.toUpperCase()}
								</Box>
							)}
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between',
							}}
						>
							<Box sx={{ display: 'flex', gap: 2 }}>
								<Box sx={{ display: 'flex', flexDirection: 'column' }}>
									<Box sx={{ display: 'flex' }}>
										<Typography
											variant="h6"
											color="#000"
											sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
										>
											{trip.departure}
										</Typography>
										<Typography
											variant="h6"
											color="rgba(0,0,0,0.2)"
											sx={{ fontWeight: 400, marginLeft: '2px' }}
										>
											-----
										</Typography>
									</Box>
								</Box>
								<Typography
									variant="h6"
									color="#000"
									sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
								>
									to
								</Typography>
								<Box sx={{ display: 'flex', flexDirection: 'column' }}>
									<Box sx={{ display: 'flex' }}>
										<Typography
											variant="h6"
											color="#000"
											sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
										>
											{trip.destination}
										</Typography>
										<Typography
											variant="h6"
											color="rgba(0,0,0,0.2)"
											sx={{ fontWeight: 400, marginLeft: '2px' }}
										>
											-----
										</Typography>
									</Box>
									<Box>
										<Typography
											variant="body2"
											color="grey"
											sx={{ fontWeight: 500 }}
										>
											{/* {moment(trip.departureTime)
														.add(4, 'hours')
														.format('LT')} */}
										</Typography>
									</Box>
								</Box>
								<Typography
									variant="body2"
									color="#ef404f"
									sx={{
										fontWeight: 500,
										marginLeft: 'auto',
										textAlign: 'left',
									}}
								>
									From KES {trip.minPrice}
								</Typography>
							</Box>

							<div
								// className={classes.tripDetails}
								style={{
									display: 'flex',
									flexDirection: 'row',
									gap: '12px',
									alignItems: 'center',
								}}
							>
								<Chip
									icon={
										<BusFront
											style={{
												fontSize: 14,
												verticalAlign: 'middle',
												color: 'f9a84e',
											}}
										/>
									}
									label={`${trip.vehicleno}`}
									sx={{ borderRadius: '6px', height: '28px' }}
								/>
								<Chip
									icon={
										<CalendarDays
											style={{
												fontSize: 13,
												verticalAlign: 'middle',
												color: 'f9a84e',
											}}
										/>
									}
									label={`${moment(trip.departureTime).format('ll')}`}
									sx={{ borderRadius: '6px', height: '28px' }}
								/>
								<Chip
									icon={
										<BusFront
											style={{
												fontSize: 14,
												verticalAlign: 'middle',
												color: 'f9a84e',
											}}
										/>
									}
									label={`${Number(trip.seats - trip.sold)} seats remaining`}
									sx={{ borderRadius: '6px', height: '28px' }}
								/>
								<Chip
									icon={
										<BusFront
											style={{
												fontSize: 14,
												verticalAlign: 'middle',
												color: 'f9a84e',
											}}
										/>
									}
									label={`Standard Class`}
									sx={{ borderRadius: '6px', height: '28px' }}
								/>
							</div>
							{/*  <Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
								}}
							>
								<Box sx={{ display: 'flex', gap: 2 }}>
									<Box sx={{ display: 'flex', flexDirection: 'column' }}>
										<Box sx={{ display: 'flex' }}>
											<Typography
												variant="h6"
												color="#000"
												sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
											>
												{trip.departure}
											</Typography>
											<Typography
												variant="h6"
												color="rgba(0,0,0,0.2)"
												sx={{ fontWeight: 400, marginLeft: '2px' }}
											>
												-----
											</Typography>
										</Box>
										<Box>
											<Typography
												variant="body2"
												color="grey"
												sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
											>
												{moment(trip.departureTime).format('LT')}
											</Typography>
										</Box>
									</Box>
									<Typography
										variant="h6"
										color="#000"
										sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
									>
										to
									</Typography>
									<Box sx={{ display: 'flex', flexDirection: 'column' }}>
										<Box sx={{ display: 'flex' }}>
											<Typography
												variant="h6"
												color="#000"
												sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
											>
												{trip.destination}
											</Typography>
											<Typography
												variant="h6"
												color="rgba(0,0,0,0.2)"
												sx={{ fontWeight: 400, marginLeft: '2px' }}
											>
												-----
											</Typography>
										</Box>
										<Box>
											<Typography
												variant="body2"
												color="grey"
												sx={{ fontWeight: 500 }}
											>
												
											</Typography>
										</Box>
									</Box>
								</Box>
								<Box style={{ display: 'flex', alignItems: 'end' }}>
									<Typography
										sx={{ fontWeight: '500' }}
										variant="h6"
										color="#ef404f"
									>
										<span>
											{new Intl.NumberFormat('en-KE', {
												style: 'currency',
												currency: 'KES',
											}).format(trip.minPrice)}
										</span>
									</Typography>
								</Box>
							</Box>
							<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
								<Box
									sx={{
										display: 'flex',
										gap: 1,
										alignItems: 'center', // Align items to the bottom
									}}
								>
									<Box
										sx={{
											width: '40px',
											height: '32px',
											backgroundColor: '#fff',
											flex: '0 0 28px',
											fontSize: '10px',
											fontWeight: '600',
											color: '#000',
											border: '1px solid green',
											borderRadius: '6px',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											padding: '10px',
										}}
									>
										4.2
									</Box>
									<Typography
										sx={{
											fontWeight: '600',
											textAlign: 'end',
											marginBottom: '0',
											lineHeight: 1, // Ensures consistent vertical alignment
										}}
										variant="body1"
									>
										Very Good
									</Typography>
									<Typography
										sx={{
											fontWeight: '600',
											color: 'grey',
											marginBottom: '0',
											lineHeight: 1, // Consistent alignment
										}}
										variant="body2"
									>
										54 reviews
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', gap: 2 }}>
									<Button
										onClick={() => handleBook(trip)}
										variant="contained"
										color="primary"
										sx={{ width: '120px', height: '32px' }}
									>
										Select Seat
									</Button>
									<Button
										onClick={() => handleBook(trip)}
										variant="contained"
										color="primary"
										sx={{ width: '120px', height: '32px' }}
									>
										Book Now
									</Button>
								</Box>
							</Box>  */}
						</Box>
					</CardContent>
					<CardActions
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							textAlign: 'left',
							gap: 2,
							padding: '0 16px',
						}}
					>
						{/* <Typography
							variant="body2"
							color="#ef404f"
							sx={{ fontWeight: 500, marginLeft: 'auto',textAlign: 'left' }}
						>
							From KES {trip.minPrice}
						</Typography> */}
						<Button
							onClick={() => handleBook(trip)}
							variant="contained"
							color="primary"
							sx={{ width: '120px', height: '32px' }}
						>
							Book Now
						</Button>
					</CardActions>
				</Card>
			</Grid>
		))
	}, [trips])

	if (loading)
		return (
			<Container className={classes.root} maxWidth={false} disableGutters>
				<Grid container spacing={2} maxWidth="xl" sx={{ padding: '0 24px' }}>
					<Box sx={{ marginTop: 2, width: '100%' }}>
						<Typography variant="h4" sx={{ fontWeight: 700, marginBottom: 2 }}>
							Featured Trips
						</Typography>
						<Typography variant="body2">
							Book world class comfortable seating
						</Typography>
					</Box>

					{[1, 2, 3].map((_, i) => (
						<Grid item xs={12} md={12} key={i}>
							<Card
								sx={{
									display: 'flex',
									gap: 2,
									height: '112px',
									justifyContent: 'center',
									maxWidth: '1400px',
									margin: 'auto',
									border: '1px solid rgba(0,0,0,0.1)',
									boxShadow: '0px 8px 32px 0px rgba(17, 34, 17, 0.05)',
								}}
							>
								<CardContent
									sx={{
										width: '100%',
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'space-between',
										padding: '16px !important',
										gap: 2,
									}}
								>
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<Skeleton variant="rectangular" width={97} height={88} />
									</Box>

									<Box
										sx={{
											flex: 1,
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'space-between',
										}}
									>
										<Box sx={{ display: 'flex', gap: 2 }}>
											<Box sx={{ display: 'flex', flexDirection: 'column' }}>
												<Box sx={{ display: 'flex' }}>
													<Skeleton variant="text" width={120} height={28} />
													<Skeleton
														variant="text"
														width={40}
														height={28}
														sx={{ ml: 1 }}
													/>
												</Box>
											</Box>
											<Skeleton variant="text" width={24} height={28} />
											<Box sx={{ display: 'flex', flexDirection: 'column' }}>
												<Box sx={{ display: 'flex' }}>
													<Skeleton variant="text" width={120} height={28} />
													<Skeleton
														variant="text"
														width={40}
														height={28}
														sx={{ ml: 1 }}
													/>
												</Box>
												<Box>
													<Skeleton variant="text" width={160} height={18} />
												</Box>
											</Box>
											<Skeleton
												variant="text"
												width={110}
												height={28}
												sx={{ marginLeft: 'auto' }}
											/>
										</Box>

										<Box
											sx={{
												display: 'flex',
												flexDirection: 'row',
												gap: '12px',
												alignItems: 'center',
											}}
										>
											<Skeleton variant="rounded" width={120} height={28} />
											<Skeleton variant="rounded" width={120} height={28} />
											<Skeleton variant="rounded" width={160} height={28} />
										</Box>
									</Box>

									<CardActions
										sx={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											textAlign: 'left',
											gap: 2,
											padding: '0 16px',
										}}
									>
										<Skeleton variant="rectangular" width={120} height={32} />
									</CardActions>
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>
			</Container>
		)
	// if (error) return <Typography color="error">{error}</Typography>

	console.log('trips', trips)

	return (
		<Container className={classes.root} maxWidth={false} disableGutters>
			<Grid container spacing={2} maxWidth="xl" sx={{ padding: '0 24px' }}>
				<Box sx={{ marginTop: 2, width: '100%' }}>
					{/* <Typography variant="h4" sx={{ fontWeight: 700 }}>
						Featured trips
					</Typography> */}
					<Typography
						variant="h4"
						sx={theme => ({
							//   fontSize: "20px",
							fontWeight: 700,
							marginBottom: theme.spacing(4),
							color: '#1a1a1a',
							position: 'relative',
							letterSpacing: '0.5px',
							'&:after': {
								content: '""',
								position: 'absolute',
								bottom: '-12px',
								left: 0,
								width: '50px',
								height: '4px',
								backgroundColor: theme.palette.primary.main,
								borderRadius: '4px',
							},
						})}
					>
						Featured Trips
					</Typography>
					<Typography variant="body2">
						Book world class comfortable seating
					</Typography>
				</Box>

				{trips && trips.length > 0 ? (
					tripList
				) : (
					<Box sx={{ width: '100%', textAlign: 'center', mt: 4 }}>
						<Typography variant="body1">
							Check back soon: Our travel experts are curating exciting new
							trips daily
						</Typography>
					</Box>
				)}
			</Grid>
			{/* <Grid container spacing={3} maxWidth="xl" sx={{ padding: '0 24px' }}>
				{trips && trips.length > 0 && (
					<Box sx={{ marginTop: 8 }}>
						<Typography variant="h4" sx={{ fontWeight: 700 }}>
							Featured trips
						</Typography>
						<Typography variant="body2">
							Book world class comfortable seating
						</Typography>
					</Box>
				)}
				{tripList}
			</Grid> */}
			{/* <Modal
				open={open}
				onClose={handleClose}
				className={classes.modal}
				aria-labelledby="route-details-modal"
				aria-describedby="route-details-description"
			>
				{selectedRoute && <RouteTrips route={selectedRoute} />}
			</Modal> */}
			<Dialog
				onClose={handleClose}
				aria-labelledby="customized-dialog-title"
				open={open}
				maxWidth="lg"
			>
				<DialogTitle id="customized-dialog-title" onClose={handleClose}>
					Available Trips
				</DialogTitle>
				<DialogContent dividers>
					{selectedRoute && <RouteTrips route={selectedRoute} />}
				</DialogContent>
				{/* <DialogActions>
					<Button autoFocus onClick={handleClose} color="primary">
						Save changes
					</Button>
				</DialogActions> */}
			</Dialog>
		</Container>
	)
}

export default Trips
